import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { usePrevious } from 'react-use'
import { bindActionCreators } from 'redux'

import { getCurrentOrganizationId } from '@/components/App/selectors'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import Page from '@/components/Page'
import { fetchAttributes } from '@/pages/OrganizationUserAttributesPage/actions'
import { FETCH_ATTRIBUTES } from '@/pages/OrganizationUserAttributesPage/actionTypes'
import {
  makeGetDisplayNamesBySchema,
  makeGetSCIMAttributeNamesBySchema
} from '@/pages/OrganizationUserAttributesPage/selectors'
import { resetRequest } from '@/store/actions'
import { isRequestError, isRequestPending, isRequestSuccessful } from '@/store/selectors'

import { createAttribute } from './actions'
import { CREATE_ATTRIBUTE } from './actionTypes'
import messages from './messages'
import UserAttributeForm, { getCustomSchemaUrn } from './UserAttributeForm'

function OrganizationUserAttributeCreatePage(props: any) {
  const intl = useIntl()

  const history = useHistory()

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([CREATE_ATTRIBUTE, FETCH_ATTRIBUTES])
    props.actions.fetchAttributes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (!prevProps.isCreateSuccessful && props.isCreateSuccessful) {
      navigateToAttributeList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, prevProps])

  const navigateToAttributeList = () => {
    history.push('/organization/users/attributes')
  }

  const onCancel = () => {
    navigateToAttributeList()
  }

  const onSubmit = (values: any) => {
    props.actions.createAttribute({
      ...values,
      id: null,
      custom: true,
      editable: true,
      multiValued: false,
      required: false,
      scimSchema: getCustomSchemaUrn(props.organizationId),
      visible: true
    })
  }

  const isDisplayNameAvailable = (value: any) => {
    return !value || !props.displayNames.some((name: any) => name.toLowerCase() === value.toLowerCase())
  }

  const isSCIMAttributeNameAvailable = (value: any) => {
    return !value || !props.scimAttributeNames.some((name: any) => name.toLowerCase() === value.toLowerCase())
  }

  return (
    <Page isLoading={props.isLoading} loadingMessage={<FormattedMessage {...messages.loading} />}>
      <Page.Header
        backNavTitle={<FormattedMessage {...messages.back} />}
        goBack={onCancel}
        title={<FormattedMessage {...messages.title} />}
      />

      {props.hasError ? (
        <ErrorConfirmation
          error={intl.formatMessage(messages.errorTitle)}
          subMessage={intl.formatMessage(messages.errorMessage)}
        />
      ) : (
        <UserAttributeForm
          onCancel={onCancel}
          onSubmit={onSubmit}
          testDisplayName={isDisplayNameAvailable}
          testSCIMAttributeName={isSCIMAttributeNameAvailable}
          types={['binary', 'boolean', 'datetime', 'decimal', 'integer', 'string']}
        />
      )}
    </Page>
  )
}

OrganizationUserAttributeCreatePage.displayName = 'OrganizationUserAttributeCreatePage'

OrganizationUserAttributeCreatePage.propTypes = {
  actions: PropTypes.shape({
    createAttribute: PropTypes.func.isRequired,
    fetchAttributes: PropTypes.func.isRequired,
    resetRequest: PropTypes.func.isRequired
  }).isRequired,
  displayNames: PropTypes.arrayOf(PropTypes.string),
  hasError: PropTypes.bool,
  isCreateSuccessful: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  organizationId: PropTypes.string.isRequired,
  scimAttributeNames: PropTypes.arrayOf(PropTypes.string)
}

OrganizationUserAttributeCreatePage.defaultProps = {
  displayNames: [],
  hasError: false,
  scimAttributeNames: []
}

const DisconnectedOrganizationUserAttributeCreatePage = OrganizationUserAttributeCreatePage
export { /** @deprecated */ DisconnectedOrganizationUserAttributeCreatePage as OrganizationUserAttributeCreatePage }

const makeMapStateToProps = () => {
  const getDisplayNamesBySchema = makeGetDisplayNamesBySchema()
  const getSCIMAttributeNamesBySchema = makeGetSCIMAttributeNamesBySchema()

  return (state: any) => {
    const organizationId = getCurrentOrganizationId(state)
    const schemaUrn = getCustomSchemaUrn(organizationId)

    return {
      organizationId,
      displayNames: getDisplayNamesBySchema(state, schemaUrn),
      hasError: isRequestError(state, [CREATE_ATTRIBUTE, FETCH_ATTRIBUTES]),
      isCreateSuccessful: isRequestSuccessful(state, CREATE_ATTRIBUTE),
      isLoading: isRequestPending(state, [CREATE_ATTRIBUTE, FETCH_ATTRIBUTES]),
      scimAttributeNames: getSCIMAttributeNamesBySchema(state, schemaUrn)
    }
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators({ createAttribute, fetchAttributes, resetRequest }, dispatch)
})

export default connect(makeMapStateToProps, mapDispatchToProps)(DisconnectedOrganizationUserAttributeCreatePage)
